/**
 * @file index.tsx
 *
 * @fileoverview Renders an organization profile teaser.
 */

import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import AnchorDiv from '~/jobiqo/components/AnchorDiv';
import Image from '~/jobiqo/components/Image';
import Link from '~/jobiqo/components/Link';
import { Image as ImageType, OrganizationProfile } from '~/jobiqo/types/types';
import { getTranslatedLink } from '~/jobiqo/utils/links';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { BriefcaseIcon } from '~/vcl/icons/Briefcase';
import { MapMarkerIcon } from '~/vcl/icons/MapMarker';
import { NetworkIcon } from '~/vcl/icons/Network';
import { OfficeIcon } from '~/vcl/icons/Office';
import { OrganizationTeaser as OrganizationTeaserCard } from '~/vcl/organisms/09-organization/01-organization-teaser';
import styles from './organizationTeaser.module.scss';
import { OrganizationPromotedIconSlot } from '~/jobiqo/components/Slots/OrganizationPromotedIconSlot';
import { OrganizationTeaserNoLogoSlot } from '~/jobiqo/components/Slots/OrganizationTeaserNoLogoSlot';
import { isSlotEmpty } from '~/jobiqo/components/Slots/utils/slotsUtils';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface OrganizationTeaserProps {
  /**
   * The organization profile which will be rendered as a teaser.
   */
  organization: OrganizationProfile;
}

export const OrganizationTeaser = ({
  organization,
}: OrganizationTeaserProps) => {
  const { ORGANIZATIONS } = useFrontendConfig();
  // Teaser cover sizes.
  const teaserCoverHeight =
    ORGANIZATIONS.ORGANIZATION_TEASER_LAYOUT.RATIO_HEIGHT;
  const teaserCoverWidth = ORGANIZATIONS.ORGANIZATION_TEASER_LAYOUT.RATIO_WIDTH;
  const ORGANIZATION_PROFILE_TEASER_FIELDS =
    ORGANIZATIONS.ORGANIZATION_TEASER_FIELDS;
  const ORGANIZATION_FLEX_ADDRESS_ENABLED =
    ORGANIZATIONS.ORGANIZATION_FLEX_ADDRESS_ENABLED;
  const ORGANIZATION_TEASER_JOBS_LINK =
    ORGANIZATIONS.ORGANIZATION_TEASER_JOBS_LINK;
  const ORGANIZATION_TEASER_DEFAULT_COVER_IMAGE =
    ORGANIZATIONS.ORGANIZATION_TEASER_DEFAULT_COVER_IMAGE;
  const teaserCover = (organization as any)?.teaserCover as ImageType;
  const teaserCoverUrl = teaserCover?.url;

  // check if the component OrganizationTeaserNoLogoSlot is implemented.
  const isOrganizationTeaserNoLogoSlotImplemented = !isSlotEmpty(
    OrganizationTeaserNoLogoSlot,
    { organization },
  );

  const messages = defineMessages({
    jobs: {
      id: 'organization_jobs_count',
      defaultMessage:
        '{count, plural, one {# open position} other {# open positions}}',
    },
  });

  // If there is no organization cover and ORGANIZATION_TEASER_DEFAULT_COVER_IMAGE is set
  // use the ORGANIZATION_TEASER_DEFAULT_COVER_IMAGE as cover image
  const coverUrl = teaserCoverUrl
    ? teaserCoverUrl
    : !!ORGANIZATION_TEASER_DEFAULT_COVER_IMAGE
    ? ORGANIZATION_TEASER_DEFAULT_COVER_IMAGE
    : null;

  return (
    <AnchorDiv
      route={organization?.url?.path}
      title={organization.name}
      style={{ display: 'flex', height: '100%' }}
    >
      <OrganizationTeaserCard
        key={organization.id}
        premium={
          ORGANIZATIONS.SHOW_PREMIUM_INDICATIONS && organization?.['isPremium']
        }
        promotedOrganizationIcon={
          <OrganizationPromotedIconSlot width="25" height="25" />
        }
        name={
          <Link href={getTranslatedLink(organization?.url?.path)}>
            {organization.name}
          </Link>
        }
        logo={
          organization?.logo?.url ? (
            <Image
              width={100}
              height={100}
              src={organization?.logo?.url}
              alt={organization.logo.alt ?? `${organization.name}`}
              {...(organization.logo.title && {
                title: organization.logo.title,
              })}
            />
          ) : isOrganizationTeaserNoLogoSlotImplemented ? (
            <OrganizationTeaserNoLogoSlot organization={organization} />
          ) : null
        }
        banner={
          coverUrl ? (
            <Image
              width={teaserCoverWidth}
              height={teaserCoverHeight}
              src={coverUrl}
              alt={teaserCover?.alt ?? `${organization.name}`}
              {...(teaserCover?.title && { title: teaserCover?.title })}
            />
          ) : (
            <div
              className={styles.emptyBanner}
              style={{
                aspectRatio: `${teaserCoverWidth} / ${teaserCoverHeight}`,
              }}
            />
          )
        }
        fields={
          <Grid columns={[2, 1]}>
            {ORGANIZATION_PROFILE_TEASER_FIELDS.organizationLocation &&
            organization.organizationLocation ? (
              <Cell alignItems="center">
                <FlexItems flexWrap="nowrap">
                  <FlexItem>
                    <MapMarkerIcon
                      height="15"
                      width="15"
                      title="Address"
                      className={styles.organizationTeaserFields}
                    />
                  </FlexItem>
                  <FlexItem>
                    <Text
                      className={styles.organizationTeaserFields}
                      margin="0"
                      marginLeft={4}
                    >
                      {ORGANIZATION_FLEX_ADDRESS_ENABLED ? (
                        <div>
                          <div>
                            {organization && (
                              <div>
                                {organization.organizationLocation?.city && (
                                  <span>
                                    {organization.organizationLocation?.city}
                                  </span>
                                )}
                                {organization.organizationLocation?.city &&
                                  organization.organizationLocation
                                    ?.countryName && <span>, </span>}
                                {organization.organizationLocation
                                  ?.countryName && (
                                  <span>
                                    {
                                      organization.organizationLocation
                                        ?.countryName
                                    }
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        organization.organizationLocation?.address
                      )}
                    </Text>
                  </FlexItem>
                </FlexItems>
              </Cell>
            ) : null}
            {ORGANIZATION_PROFILE_TEASER_FIELDS.industry &&
            organization.industry ? (
              <Cell>
                <FlexItems flexWrap="nowrap">
                  <FlexItem>
                    <OfficeIcon
                      height="15"
                      width="15"
                      title="Industry"
                      className={styles.organizationTeaserFields}
                    />
                  </FlexItem>
                  <FlexItem>
                    <Text
                      className={styles.organizationTeaserFields}
                      margin="0"
                      marginLeft={4}
                    >
                      {organization.industry?.label}
                    </Text>
                  </FlexItem>
                </FlexItems>
              </Cell>
            ) : null}
            {ORGANIZATION_PROFILE_TEASER_FIELDS.size && organization.size ? (
              <Cell>
                <FlexItems flexWrap="nowrap">
                  <FlexItem>
                    <NetworkIcon
                      height="15"
                      width="15"
                      title="Size"
                      className={styles.organizationTeaserFields}
                    />
                  </FlexItem>
                  <FlexItem>
                    <Text
                      className={styles.organizationTeaserFields}
                      margin="0"
                      marginLeft={4}
                    >
                      {organization.size?.label}
                    </Text>
                  </FlexItem>
                </FlexItems>
              </Cell>
            ) : null}
            {ORGANIZATION_PROFILE_TEASER_FIELDS.organizationJobsCount &&
            organization.organizationJobsCount ? (
              <Cell>
                <FlexItems flexWrap="nowrap" gap={4}>
                  <FlexItem>
                    <BriefcaseIcon
                      height="15"
                      width="15"
                      title="Size"
                      className={styles.organizationTeaserFields}
                    />
                  </FlexItem>
                  <FlexItem>
                    {ORGANIZATION_TEASER_JOBS_LINK ? (
                      <Link
                        href={{
                          pathname: '/jobs',
                          query: { job_organization_name: organization.name },
                        }}
                      >
                        <FormattedMessage
                          values={{ count: organization.organizationJobsCount }}
                          {...messages.jobs}
                        />
                      </Link>
                    ) : (
                      <FormattedMessage
                        values={{ count: organization.organizationJobsCount }}
                        {...messages.jobs}
                      />
                    )}
                  </FlexItem>
                </FlexItems>
              </Cell>
            ) : null}
          </Grid>
        }
      />
    </AnchorDiv>
  );
};
